import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ApplicationResponseData,
  OverviewPendingApplications,
  OverviewTechnicianData,
  RegisteredChimneySweepers,
  TechnicianApplicationData,
  TechnicianResponseData,
  UpdateDataResponse,
  UpdateTechnicianApplication,
  UpdateTechnicianApplicationRequestData,
  UpdateTechnicianApplicationResponse,
} from './employee-overview.domain';
import { CommunicationService } from '../services/communication.service';
import { CompanyData, TechnicianData } from '../domains/common.domain';
import { UserStatus } from '../constants/app.constants';

@Injectable()
export class EmployeeOverviewService {
  constructor(private communicationService: CommunicationService) {}

  getTechniciansData(paramValue: string): Observable<TechnicianResponseData> {
    return this.communicationService.getData(`technician?${paramValue}`);
  }

  getPendingApplications(paramValue: string): Observable<ApplicationResponseData> {
    return this.communicationService.getData(`technician_application?${paramValue}`);
  }

  getTechnicianApplication(id: number): Observable<ApplicationResponseData> {
    return this.communicationService.getData(`technician_application?technician_id=${id}`);
  }

  parseTabData(
    currentTabIndex: number,
    responseData: TechnicianData[] | TechnicianApplicationData[]
  ): OverviewTechnicianData[] | OverviewPendingApplications[] | RegisteredChimneySweepers[] {
    const preparedData = [];

    if (currentTabIndex === 2 || currentTabIndex === 3) {
      (responseData as TechnicianApplicationData[]).forEach(item => {
        const technicianData = (item.technician && item.technician[0]) || null;
        const companyData = (item.company && item.company[0]) || null;

        preparedData.push({
          address: companyData ? `${companyData.street} ${companyData.street_number}, ${companyData.postcode} ${companyData.city}` : '-',
          applicationProcess: !technicianData,
          technicianId: Number(item.technician_id),
          companyName: (companyData && companyData.company_name) || '-',
          name: (technicianData && `${technicianData.first_name} ${technicianData.last_name}`) || '-',
          telephone: (companyData && companyData.telephone) || '-',
          permissions: this.preparePermissionsValue(item, technicianData['technician_application'].status),
          status: item.status,
          locked: item.locked,
          lockedby: item.lockedby,
          taId: item.ta_id,
        });
      });
    } else {
      (responseData as TechnicianData[]).forEach(item => {
        preparedData.push({
          address:
            item.company && item.company[0] && item.company[0]?.postcode
              ? `${item.company[0].street} ${item.company[0].street_number}, ${item.company[0].postcode} ${item.company[0].city}`
              : '-',
          companyId: item.technician_id && Number(item.technician_id),
          companyName: (item.company && item.company[0] && item.company[0].company_name) || '-',
          dateOfExpiry: item.concession_valid_until || '-',
          name: ((item.first_name || item.last_name) && `${item.first_name} ${item.last_name}`) || '-',
          telephone: item.telephone || '-',
          permissions: this.preparePermissionsValue(item),
          status: item.status,
          email: item.email,
        });
      });
    }

    return preparedData;
  }

  updateTechnicianDetails(paramValue: string, data: TechnicianData): Observable<UpdateDataResponse> {
    const requestData = {
      technician: [data],
    };
    return this.communicationService.updateData(`technician?technician_id=${paramValue}`, requestData);
  }

  updateTechnicianApplicationStatus(paramValue: string, requestData: UpdateTechnicianApplicationRequestData): Observable<object> {
    return this.communicationService.updateData(`technician_application?${paramValue}`, requestData);
  }

  updateTechnicianStatus(technicianId: number, status: string): Observable<object> {
    const requestData = {
      technician: [
        {
          status,
        },
      ],
    };
    return this.communicationService.updateData(`technician?technician_id=${technicianId}`, requestData);
  }

  updateTechnicianApplication(taId: string, requestData: UpdateTechnicianApplication): Observable<UpdateTechnicianApplicationResponse> {
    return this.communicationService.updateData(`technician_application?ta_id=${taId}`, requestData);
  }

  updateCompanyData(companyId: string, data: CompanyData): Observable<UpdateDataResponse> {
    const requestData = {
      company: [data],
    };
    return this.communicationService.updateData(`company?company_id=${companyId}`, requestData);
  }

  private preparePermissionsValue(data: TechnicianData | TechnicianApplicationData, technicianApplicationStatus?: string): string[] {
    const allPermissions: string[] = [];
    const technicianData =
      (data &&
        (((data as TechnicianApplicationData).technician && (data as TechnicianApplicationData).technician[0]) ||
          (data as TechnicianData))) ||
      null;
    const checkValues = [
      {
        key: 'concession_water',
        value: 'water',
      },
      {
        key: 'concession_power',
        value: 'power',
      },
      {
        key: 'concession_gas',
        value: 'gas',
      },
      {
        key: 'concession_district_heating',
        value: 'heating',
      },
    ];

    if (technicianData) {
      checkValues.forEach(item => {
        if (technicianData[item.key] === '1') {
          allPermissions.push(item.value);
        }
      });

      //When the technician application status is partially issued, district heating should be approved manually by employee and the other commodities are approved automatically by API.
      if (technicianApplicationStatus === UserStatus.PARTIALLY_ISSUED) {
        allPermissions.push('heating');
      }
    }
    return allPermissions;
  }
}
